import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { useAssignableUsers } from '../users/queries';
import { useSession, useSetAbsence } from './queries';
import { Select } from '../../components/Select';

/**
 * Abwesenheit und Vertretung -- F1-29, K-70.
 *
 * **Sie steht beim eigenen Zugang und nicht in der Verwaltung.** Eine
 * Abwesenheit ist eine Aussage ueber den eigenen Kalender; wer sie fuer
 * andere setzte, verschoebe deren Arbeit ohne ihr Wissen.
 *
 * **Der Abwesende bleibt zuweisbar.** Der Server leitet die Zuweisung auf die
 * Vertretung um und vermerkt das am Ticket -- eine Zuweisung an einen
 * Abwesenden ist also zulaessig und hat eine Folge. Deshalb steht hier auch
 * kein Hinweis, man moege niemanden Abwesendem zuweisen.
 */
export function AbsencePanel() {
  const { t } = useTranslation();
  const session = useSession();
  const users = useAssignableUsers();
  const save = useSetAbsence();

  const [until, setUntil] = useState('');
  const [substitute, setSubstitute] = useState('');

  const others = (users.data ?? []).filter((user) => user.id !== session.data?.user_id);

  return (
    <section className="card">
      <h2>{t('absence.title')}</h2>
      <p className="table__muted">{t('absence.hint')}</p>

      <div className="field">
        <label className="field__label" htmlFor="absence-until">
          {t('absence.until')}
        </label>
        <input
          id="absence-until"
          type="date"
          className="field__control"
          value={until}
          onChange={(event) => {
            setUntil(event.target.value);
          }}
        />
      </div>

      <div className="field">
        <label className="field__label" htmlFor="absence-substitute">
          {t('absence.substitute')}
        </label>
        <Select
          id="absence-substitute"
          className="field__control"
          value={substitute}
          onChange={(event) => {
            setSubstitute(event.target.value);
          }}
        >
          <option value="">{t('absence.noSubstitute')}</option>
          {others.map((user) => (
            <option key={user.id} value={user.id}>
              {user.full_name}
            </option>
          ))}
        </Select>
      </div>

      {save.isError ? <ErrorNotice error={save.error} /> : null}

      <div className="row">
        <Button
          variant="primary"
          busy={save.isPending}
          onClick={() => {
            save.mutate({
              // Der Tag endet abends, nicht morgens: Wer "bis Freitag" sagt,
              // meint einschliesslich Freitag.
              out_of_office_until: until ? new Date(`${until}T23:59:59`).toISOString() : null,
              substitute_user_id: substitute || null,
            });
          }}
        >
          {t('app.save')}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            setUntil('');
            setSubstitute('');
            save.mutate({ out_of_office_until: null, substitute_user_id: null });
          }}
        >
          {t('absence.back')}
        </Button>
        {save.isSuccess ? (
          <span className="field__hint" role="status">
            {t('tickets.saved')}
          </span>
        ) : null}
      </div>
    </section>
  );
}
